import { defineStore } from "pinia";
import { FetchService } from "@/services/fetch.service";
import { authService } from "@/services/auth.service";
import { useRoute } from "vue-router";
import type { GetLogin, GetMe } from "@/api_types/teacher";
import router from "@/router";
import { useSchoolStore } from "./school";

export type Level = "parent" | "teacher" | "school" | "district" | "support";
export type TokenData = {
  iss: string;
  iat: number;
  exp: number;
  nbf: number;
  jti: string;
  sub: string;
  prv: string;
  school_id: number;
  district_id: number;
  district: string;
  integration: string;
  uid: string;
  authentication_type: Level;
  authentication_level: number;
};

// useStore could be anything like useUser, useCart
// the first argument is a unique id of the store across your application

export const useAuthenticationStore = defineStore("authentication", {
  state: () => {
    return {
      jwt: null as GetLogin | null,
      jwtData: {} as TokenData,
      modal: false,
      me: {} as GetMe,
      level: "parent" as Level,
      resetPassword: false as boolean,
      account: ""
    };
  },
  actions: {
    getName() {
      return this.me?.teacher_fname + " " + this.me?.teacher_lname;
    },
    async fetchProfile() {
      const profile = await FetchService.fetchData("/me", "get");
      if (this.level == 'support'){
        this.me = profile || {};
      }else{
        const schoolStore = useSchoolStore();
        if (!schoolStore.school || schoolStore.account !== this.account)
          await schoolStore.loadSchool(this.account);
        const integration = schoolStore.school?.integration;
        if (
          profile &&
          typeof profile !== "string" &&
          integration == "none" &&
          this.level === "teacher"
        ) {
          const [guardians, students] = await Promise.all([this.getGuardians(), this.getStudents()]);
          if (guardians?.length && typeof guardians !== "string") profile.guardians = guardians;
          if (students?.length && typeof students !== "string") profile.students = students;
        }
        this.me = profile || {};
  
        if (this.me.students?.length == 0) {
          this.modal = true;
        }
      }
    },
    async googleUrl(district_id, school_id, type) {
      const url = await FetchService.fetchRaw(
        "/google/url?district_id=" + district_id + "&school_id=" + school_id + "&type=" + type ||
          this.level
      );
      return url;
    },
    setJWT(res) {
      this.jwt = res?.access_token ? res : null;
      if (this.jwt) {
        this.jwtData = authService.parseJwt(res.access_token);
        localStorage.setItem(this.getTokenName(), JSON.stringify(res));
        return this.fetchProfile();
      } else {
        this.jwtData = {} as any;
        localStorage.setItem(this.getTokenName(), "{}");
      }
    },
    getTokenName(level?: string) {
      if (level == 'support' || this.level == 'support'){
        return `ptcwizard_${level || this.level}_jwt`;
      }else{
        return `ptcwizard_${this.account || useRoute()?.params?.account}_${level || this.level}_jwt`;
      }
    },
    isLoggedIn() {
      const jwt = this.getToken();
      if (!jwt?.access_token) return false;
      const parsed = authService.parseJwt(jwt.access_token);
      const expirationDate = new Date(parsed.exp * 1000);
      const currentDate = new Date();
      if (currentDate < expirationDate) {
        return true;
      } else {
        return false;
      }
    },

    async login(user, isDistrict?: boolean) {
      const res = await authService.login(user, isDistrict ? "district" : this.level);
      await this.setJWT(res);
      this.resetPassword = res.reset_password ? res.reset_password : false;
      return !!res;
    },

    async loginSso() {
      const route = useRoute();
      const hash = route.query.token || route.hash?.replace("#token=", "") || null;
      const token = authService.loginFromHash(hash);
      this.setJWT(token);
      return !!token;
    },
    loadToken() {
      const data = localStorage.getItem(this.getTokenName());
      this.setJWT(data ? JSON.parse(data) : null);
    },
    resetPasswordRequired() {
      return this.resetPassword;
    },
    getToken() {
      if (this.jwt) return this.jwt;
      else {
        this.loadToken();
        return this.jwt;
      }
    },

    logout() {
      this.setJWT(null);
    },

    pushRoute(to: string) {
      if (!this.level) router.push(to || "/");
    if (this.level == 'support'){
      router.push(`/${this.level}/${to.replace(/^\//, "")}`);
    } else   if (this.level == "parent"){
        router.push(`/${this.account}/${to.replace(/^\//, "")}`);
      }else{
        router.push(`/${this.account}/${this.level}/${to.replace(/^\//, "")}`);
      }
    },
    getGuardians() {
      return FetchService.fetchData("/guardians", "get");
    },
    getStudents() {
      return FetchService.fetchData("/students", "get");
    }
  }
});
